import { useCallback, useRef, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDialogFocusRestore } from '@/lib/hooks/useDialogFocusRestore';
import type { Service } from '@/types/stack';

interface ScaleServiceDialogProps {
  service: Service;
  open: boolean;
  onClose: () => void;
  /** Rejecting keeps the dialog open and shows the error message inline. */
  onScale: (replicas: number) => Promise<void>;
}

/** The form is mounted only while open, so every open starts from the service's current replicas (no reset effect). */
export function ScaleServiceDialog(props: ScaleServiceDialogProps) {
  const submittingRef = useRef(false);
  const { open, onClose } = props;
  const { captureOpener, restoreFocus } = useDialogFocusRestore(); // lives in the shell: outlives ScaleForm
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !submittingRef.current) onClose();
      }}
    >
      {open ? <ScaleForm {...props} submittingRef={submittingRef} captureOpener={captureOpener} restoreFocus={restoreFocus} /> : null}
    </Dialog>
  );
}

function parseReplicas(text: string): number | null {
  const t = text.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return Number.isSafeInteger(n) ? n : null;
}

function ScaleForm({
  service,
  onScale,
  onClose,
  submittingRef,
  captureOpener,
  restoreFocus,
}: ScaleServiceDialogProps & {
  submittingRef: { current: boolean };
  captureOpener: () => void;
  restoreFocus: (event: Event) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const focusAlert = useCallback((el: HTMLParagraphElement | null) => { el?.focus(); }, []);
  const [text, setText] = useState(String(service.replicas));
  const [attempted, setAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorSeq, setErrorSeq] = useState(0);

  const parsed = parseReplicas(text);
  const invalid = attempted && parsed === null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    setAttempted(true);
    if (parsed === null) {
      inputRef.current?.focus();
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await onScale(parsed);
      submittingRef.current = false;
      onClose();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : 'Failed to scale service');
      setErrorSeq((n) => n + 1);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const block = (e: Event) => {
    if (submittingRef.current) e.preventDefault();
  };

  return (
    <DialogContent
      className="sm:max-w-md"
      onOpenAutoFocus={(e) => {
        captureOpener();
        e.preventDefault();
        inputRef.current?.select();
        inputRef.current?.focus();
      }}
      onCloseAutoFocus={restoreFocus}
      onEscapeKeyDown={block}
      onPointerDownOutside={block}
      onInteractOutside={block}
    >
      <DialogHeader>
        <DialogTitle>Scale service</DialogTitle>
        <DialogDescription>Set the replica count for '{service.name}'.</DialogDescription>
      </DialogHeader>
      <form onSubmit={(e) => void handleSubmit(e)} noValidate className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Current replicas: <span className="tabular-nums font-medium text-foreground">{service.replicas}</span>
        </p>
        <div className="space-y-2">
          <Label htmlFor="scale-replicas">Replicas</Label>
          <Input
            ref={inputRef}
            id="scale-replicas"
            type="number"
            inputMode="numeric"
            min={0}
            step={1}
            value={text}
            onChange={(e) => setText(e.target.value)}
            required
            aria-required="true"
            aria-invalid={invalid}
            aria-describedby={invalid ? 'scale-replicas-error' : undefined}
            readOnly={submitting}
            className="tabular-nums"
          />
          {invalid ? (
            <p id="scale-replicas-error" className="text-sm text-destructive">
              Enter a whole number, 0 or greater.
            </p>
          ) : null}
        </div>

        {error ? (
          <p
            key={errorSeq}
            ref={focusAlert}
            tabIndex={-1}
            role="alert"
            className="text-sm text-destructive outline-none"
          >
            {error}
          </p>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" aria-disabled={submitting} onClick={() => { if (!submitting) onClose(); }}>
            Cancel
          </Button>
          <Button type="submit" aria-disabled={submitting} aria-busy={submitting} className={submitting ? 'pointer-events-none opacity-70' : undefined}>
            {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {submitting ? 'Scaling…' : 'Scale'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
